import { precoDigitado } from '../precos/entradaPreco'
import { formatarMoeda } from '../lib/formatos'
import { converterQuantidadeRegra } from '../importacao/validacao'

// Decide o que fazer com o preco digitado no CADASTRO MANUAL do produto.
//
// Por que isto existe: ate 16/08/2026 a tela de cadastro de produto nao tinha
// campo de preco nenhum. O preco nao mora no produto -- mora na tabela `precos`,
// que amarra tabela de preco + produto + preco -- entao produto cadastrado a mao
// nascia SEM preco em tabela nenhuma, e no pedido ele aparecia sem valor. O
// representante cadastrava e nao conseguia vender. Achado dela no smoke do beta.
//
// A regra que mais importa aqui e a do preco sem tabela: digitar um valor e nao
// escolher a tabela nao pode gravar o produto em silencio deixando o preco cair
// no chao. Esse era o jeito comodo de escrever isto, e teria devolvido o mesmo
// defeito com outra cara -- o representante digita o preco, ve "Salvo", e o
// produto segue sem valor. As duas mutacoes (cair no chao, e preco invalido
// virar zero) foram aplicadas e o teste pegou as duas.

export type ResultadoPreco =
  | { tipo: 'sem_preco' }
  | { tipo: 'gravar'; tabelaId: string; preco: number; minima: number | null; maxima: number | null }
  | { tipo: 'erro'; mensagem: string }

export function decidirPrecoDoCadastro(
  tabelaId: string,
  textoPreco: string,
  // A regra de quantidade daquele preco ("so vale de 24 unidades para cima").
  // Opcionais com default vazio: quem chamava com dois argumentos continua
  // valendo, e vazio significa "este preco vale para qualquer quantidade".
  textoMinima = '',
  textoMaxima = '',
): ResultadoPreco {
  const temTexto = textoPreco.trim() !== ''

  // Nao digitou preco: o produto pode ser salvo sem preco, como sempre foi.
  // Isso vale mesmo com tabela escolhida -- escolher a tabela e nao digitar
  // nada nao e erro, e so nao ter preco ainda.
  //
  // ⚠ Este return vem ANTES de olhar minima/maxima de proposito: sem preco nao
  // existe linha de `precos` onde pendurar a regra, e gravar o produto deixando
  // a quantidade cair no chao seria o mesmo defeito que este modulo nasceu para
  // impedir, so que com outra cara.
  if (!temTexto) return { tipo: 'sem_preco' }

  if (tabelaId === '') {
    return { tipo: 'erro', mensagem: 'Escolha a tabela de preço antes de informar o valor.' }
  }

  const preco = precoDigitado(textoPreco)
  if (preco === null) {
    // ⚠ Recusar, e nao gravar 0: um produto a R$ 0,00 entra no pedido e sai na
    // nota sem ninguem estranhar.
    return { tipo: 'erro', mensagem: 'Preço inválido. Use o formato 1.234,56.' }
  }

  // `converterQuantidadeRegra` devolve undefined para campo vazio (o normal) e
  // null para valor sujo. So o null e erro.
  const minima = converterQuantidadeRegra(textoMinima)
  if (minima === null) {
    return { tipo: 'erro', mensagem: 'Quantidade mínima inválida. Use um número maior que zero.' }
  }
  const maxima = converterQuantidadeRegra(textoMaxima)
  if (maxima === null) {
    return { tipo: 'erro', mensagem: 'Quantidade máxima inválida. Use um número maior que zero.' }
  }
  if (minima !== undefined && maxima !== undefined && maxima < minima) {
    // Regra que nenhuma quantidade satisfaz: o item avisaria para sempre.
    return { tipo: 'erro', mensagem: 'A quantidade máxima não pode ser menor que a mínima.' }
  }

  return { tipo: 'gravar', tabelaId, preco, minima: minima ?? null, maxima: maxima ?? null }
}

export interface DicaDoPreco {
  /** O que o rotulo "Preço (R$)" pode acrescentar sem mentir. */
  sufixo: string
  /** Falso enquanto nao houver tabela escolhida: nao ha preco a editar ainda. */
  campoLigado: boolean
  /** O numero de exemplo dentro do campo vazio. Vazio quando desligado. */
  exemplo: string
}

// ▶ O que o campo de preco do cadastro pode AFIRMAR, em cada estado.
//
// Nasceu de um achado dela em 02/09/2026: no celular, o card da lista mostrava
// R$ 22,50 e a ficha do mesmo produto mostrava "0,00". Nenhum dos dois estava
// gravado errado -- o "0,00" era o placeholder de um campo vazio, e o campo
// estava vazio porque a Forte Pan tem 5 tabelas e nenhuma vem escolhida.
// Numero de exemplo dentro de um campo rotulado "Preço" nao se le como exemplo:
// le-se como preco. E duas telas do mesmo dado discordando ja custou caro aqui.
//
// A frase de sem-tabela e IRMA da que `decidirPrecoDoCadastro` devolve ao
// salvar ("Escolha a tabela de preço antes de informar o valor"): a tela avisa
// antes o que o salvamento so diria depois.
export function dicaDoPreco(tabelaId: string, precoAtual: number | null): DicaDoPreco {
  if (tabelaId === '') {
    return {
      sufixo: ' — escolha a tabela para ver o preço',
      campoLigado: false,
      exemplo: '',
    }
  }
  return {
    // ⚠ `!= null` de proposito, para o ZERO gravado aparecer como 0,00 e nao
    // cair em "sem preço nesta tabela" -- inverteria a mentira em vez de
    // desfaze-la. Zero e preco de verdade neste sistema.
    sufixo: precoAtual != null ? ` — atual: ${formatarMoeda(precoAtual)}` : ' — sem preço nesta tabela',
    campoLigado: true,
    exemplo: '0,00',
  }
}
